import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ROUTES } from '@/shared/config/routes';
import { Spinner } from '@/shared/ui';

import { useClientSession, useMasterSession } from '../hooks/useSession';

/** Shown while the server is asked who is signed in. */
const Checking = () => (
  <div className="flex min-h-screen items-center justify-center bg-white">
    <Spinner size="lg" />
  </div>
);

/**
 * Guards the master dashboard.
 *
 * `replace` keeps the guarded URL out of history, so the back button after
 * signing out does not land on a page that will only bounce again.
 *
 * The destination travels as a `redirect` query rather than router state:
 * state is lost on a reload, and an invitation link opened from an email is
 * exactly the visit that may need to register first and come back.
 */
export const RequireMaster = () => {
  const { isAuthenticated, isLoading } = useMasterSession();
  const location = useLocation();

  if (isLoading) return <Checking />;
  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);

    return <Navigate to={`${ROUTES.auth}?redirect=${redirect}`} replace />;
  }

  return <Outlet />;
};

/**
 * Guards the client hub, which sends people to the passwordless sign-in —
 * returning them by the same `?redirect=` query as the master guard.
 */
export const RequireClient = () => {
  const { isAuthenticated, isLoading } = useClientSession();
  const location = useLocation();

  if (isLoading) return <Checking />;
  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);

    return <Navigate to={`${ROUTES.clientLogin}?redirect=${redirect}`} replace />;
  }

  return <Outlet />;
};
